import { createSlice, PayloadAction } from '@reduxjs/toolkit';
// Adjust import paths as necessary
import {
  initialPreviewState,
  initialTileState,
  TileBuilderState,
  TilePreviewState,
  Transformation
} from '../../types/tileBuilderTypes';
import { TileDisplayContext } from '../tileDisplayContextUtils';
import { TileRuntimeContext } from '../tileRuntimeContext';
import { calculateRealLeaderLayout } from '../../components/Admin/TileBuilder/TilePreview/tilePreviewHelpers';


export interface TileBuilderSliceEntry {
  tile: TileBuilderState;
  preview: TilePreviewState;
}

export type TileBuilderSliceState = {
  [chartId: string]: TileBuilderSliceEntry;
};

const initialState: TileBuilderSliceState = {};

const tileBuilderSlice = createSlice({
  name: 'tileBuilder',
  initialState,
  reducers: {
    // --- Instance Management ---
    addTileInstance(state, action: PayloadAction<{ chartId: string }>) {
      const { chartId } = action.payload;
      state[chartId] = { tile: { ...initialTileState }, preview: { ...initialPreviewState } };
    },
    removeTileInstance(state, action: PayloadAction<{ chartId: string }>) {
      const { chartId } = action.payload;
      delete state[chartId];
    },
    transferTileInstance(state, action: PayloadAction<{ tempId: string, realId: string }>) {
      const { tempId, realId } = action.payload;
      if (state[tempId]) {
        state[realId] = state[tempId];
        delete state[tempId];
      }
    },

    // --- Tile State Operations ---
    setTileName(state, action: PayloadAction<{ chartId: string, tileName: string }>) {
      const { chartId, tileName } = action.payload;
      if (state[chartId]) state[chartId].tile.tileName = tileName;
    },
    setTileDescription(state, action: PayloadAction<{ chartId: string, tileDescription: string }>) {
      const { chartId, tileDescription } = action.payload;
      if (state[chartId]) state[chartId].tile.tileDescription = tileDescription;
    },
    setChartType(state, action: PayloadAction<{ chartId: string, chartType: string }>) {
      const { chartId, chartType } = action.payload;
      if (state[chartId]) state[chartId].tile.chartType = chartType;
    },
    addQuery: (state, action) => {
      state[action.payload.chartId].tile.queries.push({ ...action.payload.query });
    },
    updateQuery: (state, action) => {
      state[action.payload.chartId].tile.queries[action.payload.index] = { ...action.payload.query };
    },
    setQueries: (state, action) => {
      state[action.payload.chartId].tile.queries = action.payload.queries.map(q => ({ ...q }));
    },
    setTransformations(state, action: PayloadAction<{ chartId: string, transformations: Transformation[] }>) {
      const { chartId, transformations } = action.payload;
      if (state[chartId]) state[chartId].tile.transformations = transformations;
    },
    removeQuery(state, action: PayloadAction<{ chartId: string, index: number }>) {
      const { chartId, index } = action.payload;
      if (state[chartId]) {
        state[chartId].tile.queries = state[chartId].tile.queries.filter((_, i) => i !== index);
      }
    },
    setQueryResult(state, action: PayloadAction<{ chartId: string, index: number, result: any }>) {
      const { chartId, index, result } = action.payload;
      if (state[chartId] && state[chartId].tile.queries[index]) {
        state[chartId].tile.queries[index].queryResult = result;
      }
    },
    addTransformation(state, action: PayloadAction<{ chartId: string, transformation: Transformation }>) {
      const { chartId, transformation } = action.payload;
      if (state[chartId]) state[chartId].tile.transformations.push(transformation);
    },
    updateTransformation(state, action: PayloadAction<{ chartId: string, index: number, transformation: Transformation }>) {
      const { chartId, index, transformation } = action.payload;
      if (state[chartId] && state[chartId].tile.transformations[index]) {
        state[chartId].tile.transformations[index] = transformation;
      }
    },
    removeTransformation(state, action: PayloadAction<{ chartId: string, index: number }>) {
      const { chartId, index } = action.payload;
      if (state[chartId]) {
        state[chartId].tile.transformations.splice(index, 1);
      }
    },
    setFields(state, action: PayloadAction<{ chartId: string, fields: string[] }>) {
      const { chartId, fields } = action.payload;
      if (state[chartId]) {
        state[chartId].tile.fields = fields;
      }
    },
    setSettings(state, action: PayloadAction<{ chartId: string, settings: Partial<TileBuilderState['settings']> }>) {
      const { chartId, settings } = action.payload;
      if (state[chartId]) {
        let changed = false;
        const prev = state[chartId].tile.settings;

        // Compare each key in settings
        for (const key in settings) {
          const newVal = settings[key];
          const oldVal = prev[key];

          // Special case for arrays: do deep compare
          if (Array.isArray(newVal) && Array.isArray(oldVal)) {
            if (newVal.length !== oldVal.length || newVal.some((v, i) => v !== oldVal[i])) {
              changed = true;
              break;
            }
          } else if (newVal !== oldVal) {
            changed = true;
            break;
          }
        }

        // Only update if changed
        if (changed) {
          state[chartId].tile.settings = { ...prev, ...settings };
        }
      }
    },
    resetTileState(state, action: PayloadAction<{ chartId: string }>) {
      const { chartId } = action.payload;
      if (state[chartId]) {
        state[chartId] = { tile: { ...initialTileState }, preview: { ...initialPreviewState } };
      }
    },

    // --- Preview States ---
    setDisplayContext(state, action: PayloadAction<{ chartId: string; displayContext: TileDisplayContext }>) {
      const { chartId, displayContext } = action.payload;
      state[chartId].preview.displayContext = displayContext;

      // Recompute leaderLayout when displayContext changes
      const s = state[chartId].tile.settings as any;
      if (displayContext === 'leader') {
        state[chartId].preview.leaderLayout = calculateRealLeaderLayout(
          s.leaderStripCount || 1,
          s.leaderPreviewHeight || 300,
          700
        );
      } else {
        state[chartId].preview.leaderLayout = undefined;
      }
    },
    setRuntimeContext(state, action: PayloadAction<{ chartId: string; context: TileRuntimeContext }>) {
      state[action.payload.chartId].preview.runtimeContext = action.payload.context;
    },
    setSelectedSourceIdx(state, action: PayloadAction<{ chartId: string; index: number }>) {
      state[action.payload.chartId].preview.selectedSourceIdx = action.payload.index;
    },
    setLeaderLayout(state, action: PayloadAction<{ chartId: string }>) {
      const { chartId } = action.payload;
      const s = state[chartId].tile.settings as any;
      if (state[chartId].preview.displayContext === 'leader') {
        state[chartId].preview.leaderLayout = calculateRealLeaderLayout(
          s.leaderStripCount || 1,
          s.leaderPreviewHeight || 300,
          700
        );
      }
    },
  },
});

export const {
  addTileInstance,
  removeTileInstance,
  transferTileInstance,
  setTileName,
  setTileDescription,
  setChartType,
  setQueries,
  setTransformations,
  addQuery,
  updateQuery,
  removeQuery,
  setQueryResult,
  addTransformation,
  updateTransformation,
  removeTransformation,
  setFields,
  setSettings,
  resetTileState,
  setDisplayContext,
  setRuntimeContext,
  setSelectedSourceIdx,
  setLeaderLayout,
} = tileBuilderSlice.actions;

export default tileBuilderSlice.reducer;